import { usePlayerStore } from '@/stores/playerStore';
import { getPlayerEventManager } from '@/utils/playerEventManager';
import { getCookieAmend, getQueryVariable } from '@/utils/apiUtils';
import { getPlatformType } from '@/utils/platformUtils';
import { TVKPlatformInfo, prodLineKeyToPlayPlatformKey } from '@/utils/constants';
import { logger } from '@/utils/devUtils';
import { getStartTimeAndType } from '@/utils/playerUtils';
import { playerErrorMonitor } from './playerErrorMonitor';
import { getErrorInfo } from './playerErrorCodes';
import { getPlayerHistoryManager, initPlayerHistoryManager } from './playerHistoryManager';
import { toast } from '@/components/common';
import i18n from '@/i18n/config';
import { getPlayUrl } from '@/services/playApi';

/**
 * 全局播放器管理器
 * 单例模式，确保整个应用只有一个播放器实例
 * 负责播放器的创建、初始化和销毁
 */
class GlobalPlayerManager {
  private static instance: GlobalPlayerManager;

  /**
   * 获取单例实例
   */
  static getInstance(): GlobalPlayerManager {
    if (!GlobalPlayerManager.instance) {
      GlobalPlayerManager.instance = new GlobalPlayerManager();
    }
    return GlobalPlayerManager.instance;
  }

  private playerInstance: any = null;
  private containerElement: HTMLDivElement | null = null;
  private isInitializing = false;
  private initPromise: Promise<any> | null = null;

  private constructor() {
    // 私有构造函数，防止外部实例化
  }

  /**
   * 获取或创建全局 DOM 容器
   * 容器挂载在 document.body 下
   * 容器一直存在，默认隐藏，通过 showContainer/hideContainer 方法控制显示/隐藏
   */
  getOrCreateContainer(): HTMLDivElement {
    if (this.containerElement && document.body.contains(this.containerElement)) {
      return this.containerElement;
    }

    // 创建全局容器，默认隐藏
    const container = document.createElement('div');
    container.id = 'global-player-container';
    // 增加 dt-pgid 属性用于平台标识
    container.setAttribute('dt-pgid', 'p_linux_play_container');
    container.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; display: none;';
    document.body.appendChild(container);
    this.containerElement = container;
    logger.log('[player] 全局容器已创建（默认隐藏）');
    return container;
  }

  /**
   * 显示播放器容器
   * 通过事件调用此方法来显示容器
   */
  showContainer() {
    const container = this.containerElement || this.getOrCreateContainer();
    if (container.style.display !== '') {
      container.style.display = '';
      logger.log('[player] 显示播放器容器');
    }
  }

  /**
   * 隐藏播放器容器
   * 通过事件调用此方法来隐藏容器
   */
  hideContainer() {
    if (this.containerElement && this.containerElement.style.display !== 'none') {
      this.containerElement.style.display = 'none';
      logger.log('[player] 隐藏播放器容器');
    }
  }

  /**
   * 更新容器的显示/隐藏状态
   * @param visible 是否显示
   */
  updateContainerVisibility(visible: boolean) {
    visible ? this.showContainer() : this.hideContainer();
  }

  /**
   * 确保播放器已初始化（懒加载）
   * 如果已存在实例，直接返回
   * 如果正在初始化，等待初始化完成
   * 如果未初始化，开始初始化
   */
  async ensurePlayer(cid: string, vid: string): Promise<any> {
    // 如果播放器已存在，直接返回
    if (this.playerInstance) {
      logger.log('[player] 播放器实例已存在，复用现有实例');
      return this.playerInstance;
    }

    // 如果正在初始化，等待初始化完成
    if (this.isInitializing && this.initPromise) {
      logger.log('[player] 播放器正在初始化，等待完成...');
      return this.initPromise;
    }

    // 开始初始化
    this.isInitializing = true;
    this.initPromise = this.createPlayer(cid, vid);

    try {
      this.playerInstance = await this.initPromise;
      (window as any).playerInstance = this.playerInstance;
      logger.log('[player] 播放器初始化成功');
      return this.playerInstance;
    } catch (error) {
      logger.error('[player] 播放器初始化失败:', error);

      // 记录错误
      playerErrorMonitor.logError({
        type: 'init',
        code: 'INIT_FAILED',
        message: error instanceof Error ? error.message : String(error),
        context: { cid, vid },
      });

      throw error;
    } finally {
      this.isInitializing = false;
      this.initPromise = null;
    }
  }

  /**
   * 销毁播放器（仅在应用退出时调用）
   */
  destroy() {
    logger.log('[player] 开始清理播放器');

    // 1. 销毁播放器实例
    if (this.playerInstance) {
      try {
        if (typeof this.playerInstance.destroy === 'function') {
          this.playerInstance.destroy();
        }
        this.playerInstance = null;
        logger.log('[player] 播放器实例已销毁');
      } catch (error) {
        logger.error('[player] 销毁播放器实例失败:', error);
      }
    }

    // 2. 清理 DOM 容器内容（但保留容器本身）
    if (this.containerElement) {
      this.containerElement.innerHTML = '';
      // 注意：不删除容器元素，容器一直存在，只隐藏
      logger.log('[player] DOM 容器内容已清理');
    }

    // 3. 退出时上报历史记录（仅全屏模式下才上报，在重置状态前取 cid/vid）
    try {
      const historyManager = getPlayerHistoryManager();
      if (historyManager.isLastPlayModeFullscreen()) {
        const { cid, vid } = usePlayerStore.getState();
        if (cid && vid) {
          historyManager.reportOnExit(cid, vid).catch((err) => logger.error('[player] 退出时上报历史失败:', err));
        }
      }
    } catch (error) {
      logger.error('[player] 退出时上报历史失败:', error);
    }

    // 4. 重置状态
    try {
      usePlayerStore.getState().reset();
      logger.log('[player] PlayerStore 状态已重置');
    } catch (error) {
      logger.error('[player] 重置 PlayerStore 失败:', error);
    }

    // 5. 清理事件管理器
    try {
      getPlayerEventManager().cleanup();
      logger.log('[player] 事件管理器已清理');
    } catch (error) {
      logger.error('[player] 清理事件管理器失败:', error);
    }

    // 6. 确保容器隐藏
    this.hideContainer();

    logger.log('[player] 清理完成');
  }

  /**
   * 获取当前播放器实例（用于调试）
   */
  getPlayerInstance(): any {
    return this.playerInstance;
  }

  /**
   * 统一的视频播放方法
   * @param player 播放器实例
   * @param vid 视频ID
   * @param cid 内容ID
   * @param startTime 可选，指定起播时间（秒）；不传则按历史/片头逻辑；传 0 表示从头播放（如重新播放）
   * @returns 播放结果
   */
  public async playVideo({
    player,
    vid,
    cid,
    startTime: startTimeOverride,
  }: {
    player: any;
    vid: string;
    cid: string;
    startTime?: number;
  }): Promise<any> {
    if (typeof player?.play !== 'function') {
      logger.warn('[player] 播放器不支持 play 方法');
      return null;
    }

    // 先获取播放详情数据，确保 skipStart 等信息是最新的
    let skipStart = 0;
    try {
      const playDetailData = await getPlayUrl(cid, vid);
      if (playDetailData?.video_data?.current_video_data) {
        const currentVideoData = playDetailData.video_data.current_video_data;

        // 批量提取所需字段，减少重复属性访问
        const skipEndSeconds = currentVideoData.skip_end || 0;
        const skipHeadBeginSeconds = currentVideoData.skip_head_begin || 0;
        const skipStartSeconds = currentVideoData.skip_start || 0;
        const skipTailEndSeconds = currentVideoData.skip_tail_end || 0;
        const paymentType = currentVideoData?.payment_type || 0;
        const videoTitle = currentVideoData.title;
        const playListInfo = playDetailData.play_list_info;

        // 一次性获取 store state，避免多次调用 getState()
        const store = usePlayerStore.getState();

        // 并行执行独立的 store 更新，减少串行调用开销
        store.setSkipIntro(skipEndSeconds, skipHeadBeginSeconds, skipStartSeconds, skipTailEndSeconds);
        store.setVideoInfo(cid, vid, videoTitle);
        store.setVideoListInfo(playListInfo);
        store.setPayStatus(paymentType);

        skipStart = skipStartSeconds;
        logger.log('[player] 播放详情数据已加载:', {
          skipStart,
          skipEnd: skipEndSeconds,
          skipHeadBegin: skipHeadBeginSeconds,
          skipTailEnd: skipTailEndSeconds,
        });
      }
    } catch (error) {
      logger.warn('[player] 获取播放详情数据失败，使用默认值:', error);
      // 获取失败不影响播放，继续使用默认值
      skipStart = usePlayerStore.getState().skipStart;
    }

    // 一次性获取所需状态，避免多次调用 getState()
    const { playerKernel, skipIntroEnabled } = usePlayerStore.getState();
    const guid = (getCookieAmend('guid') || '').toUpperCase();
    const countryCode = getCookieAmend('country_code') || '';
    const langCode = getCookieAmend('lang_code') || 'zh-CN';
    const resolved =
      startTimeOverride !== undefined
        ? { startTime: startTimeOverride, type: 'default' as const }
        : getStartTimeAndType(cid, vid, skipStart);
    const { startTime, type } = resolved;
    const result = await player.play(vid, {
      videoReqInfoParam: {
        cid,
        guid,
        country_code: countryCode,
        language_code: langCode,
      },
      videoLoadParam: {
        kernelName: playerKernel,
        startTime,
      },
    });

    logger.log('[player] 视频播放调用成功:', result);

    // 根据起播类型显示相应的 toast 提示
    if (type === 'history' && startTime > 0) {
      // 从历史记录继续播放
      setTimeout(() => {
        toast.show(i18n.t('player.resumeFromHistoryToast'));
      }, 500);
    } else if (skipIntroEnabled && type === 'skip_intro' && startTime > 0) {
      // 跳过片头
      setTimeout(() => {
        toast.show(i18n.t('player.skipIntroToast'));
      }, 500);
    }

    return result;
  }

  /**
   * 创建播放器实例
   */
  private async createPlayer(cid: string, vid: string): Promise<any> {
    logger.log('[player] 开始创建播放器实例');

    // 确保容器存在
    const container = this.getOrCreateContainer();

    // 创建播放器容器
    const playerWrapper = document.createElement('div');
    playerWrapper.id = 'player-wrapper';
    playerWrapper.style.cssText = 'width: 100%; height: 100%;';
    container.innerHTML = '';
    container.appendChild(playerWrapper);

    try {
      // 从 URL 获取播放器配置，如果 URL 没有则从 localStorage 读取
      const playenv = getQueryVariable('playenv') || localStorage.getItem('playenv') || 'prod';
      const playver = getQueryVariable('playver') || localStorage.getItem('playver');

      // 构建播放器脚本 URL
      const playerScriptUrl = playver
        ? `https://static.wetvinfo.com/libs/wetv-player/${playver}/player-light.js`
        : 'https://static.wetvinfo.com/libs/wetv-player/latest/player-light.js';

      // 检查是否已加载播放器脚本
      const createH5Player = (window as any)?.ThumbPlayer?.createH5Player;
      if (!createH5Player) {
        // 动态加载播放器脚本
        await new Promise<void>((resolve, reject) => {
          // 检查脚本是否已经存在
          const existingScript = document.querySelector('script[src*="player-light.js"]');
          if (existingScript) {
            if ((window as any)?.ThumbPlayer?.createH5Player) {
              resolve();
              return;
            }
            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', () => {
              const errorMsg = `播放器脚本加载失败: ${playerScriptUrl}`;
              logger.error('[player]', errorMsg);
              reject(new Error(errorMsg));
            });
            return;
          }

          const script = document.createElement('script');
          script.src = playerScriptUrl;
          script.onload = () => resolve();
          script.onerror = () => {
            const errorMsg = `播放器脚本加载失败: ${playerScriptUrl}`;
            logger.error('[player]', errorMsg);
            reject(new Error(errorMsg));
          };
          document.head.appendChild(script);
        });

        // 使用 polling 等待 window.ThumbPlayer 就绪，最多等待 2 秒，每 50ms 检查一次
        // 替代原来硬编码的 100ms 延迟，更可靠且通常更快
        await new Promise<void>((resolve, reject) => {
          const maxWaitMs = 2000;
          const intervalMs = 50;
          let elapsed = 0;
          const timer = setInterval(() => {
            if ((window as any)?.ThumbPlayer?.createH5Player) {
              clearInterval(timer);
              resolve();
              return;
            }
            elapsed += intervalMs;
            if (elapsed >= maxWaitMs) {
              clearInterval(timer);
              reject(new Error('播放器脚本加载超时：window.ThumbPlayer 未在 2 秒内就绪'));
            }
          }, intervalMs);
        });
      }

      const createH5PlayerFn = (window as any)?.ThumbPlayer?.createH5Player;
      if (!createH5PlayerFn) {
        throw new Error('播放器创建函数不存在');
      }

      // 构建播放器配置
      // 动态获取 appid，根据平台类型
      const platformType = getPlatformType();
      const appid = platformType === 'wetv' || platformType === 'iflix' ? platformType : 'wetv';
      const playerConfig: any = {
        type: 'vod',
        platform: prodLineKeyToPlayPlatformKey[platformType],
        appid,
        disableReport: false,
      };

      if (playenv) {
        playerConfig.env = playenv;
      }

      // 创建播放器实例
      const player = createH5PlayerFn(
        {
          container: '#player-wrapper',
          enableMediaFragmentsURI: false,
          hlsjsKernelConfig: {
            maxBufferLength: 30,
            backBufferLength: 20,
            maxBufferSize: 30,
            progressive: false,
          },
          offlineConfig: {
            platform: TVKPlatformInfo[platformType],
          },
        },
        playerConfig
      );

      // 将播放器实例保存到状态管理
      usePlayerStore.getState().setPlayerInstance(player);

      // 初始化播放器事件管理器
      const eventManager = getPlayerEventManager();
      eventManager.init(player);

      // 初始化播放历史记录管理器（如果尚未初始化）
      initPlayerHistoryManager();

      // 设置错误监听
      this.setupErrorListeners(player, cid, vid);

      // 设置视频信息
      usePlayerStore.getState().setVideoInfo(cid, vid);
      // 清除之前的错误信息
      usePlayerStore.getState().clearError();

      return player;
    } catch (error) {
      logger.error('[player] 创建播放器失败:', error);
      throw error;
    }
  }

  /**
   * 设置播放器错误监听
   * 参考 tv-linux/src/views/newParticular/newParticular.js:5699-5740
   */
  private setupErrorListeners(player: any, cid: string, vid: string) {
    // 播放错误
    player.on('error', (error: any) => {
      const errorCode = error.code || error.dataCode || 'UNKNOWN';
      const errorExem = error.exem;

      // 记录错误
      playerErrorMonitor.logError({
        type: 'play',
        code: errorCode,
        message: error.message || '播放错误',
        context: {
          cid,
          vid,
          errorData: error,
          exem: errorExem,
        },
      });

      // 根据错误码获取错误信息
      const errorInfo = getErrorInfo(errorCode, errorExem);

      // 更新播放器状态
      usePlayerStore.getState().setError(errorCode, 'play', errorInfo);

      logger.error('[player] 播放错误:', {
        code: errorCode,
        exem: errorExem,
        message: errorInfo.message,
      });
    });

    // 网络错误
    player.on('networkerror', (error: any) => {
      const errorCode = error.code || 'NETWORK_ERROR';

      // 记录错误
      playerErrorMonitor.logError({
        type: 'network',
        code: errorCode,
        message: error.message || '网络错误',
        context: {
          cid,
          vid,
          url: error.url,
        },
      });

      // 根据错误码获取错误信息
      const errorInfo = getErrorInfo(errorCode);

      // 更新播放器状态
      usePlayerStore.getState().setError(errorCode, 'network', errorInfo);

      logger.error('[player] 网络错误:', {
        code: errorCode,
        message: errorInfo.message,
      });
    });

    // 解码错误
    player.on('decodeerror', (error: any) => {
      const errorCode = error.code || 'DECODE_ERROR';

      // 记录错误
      playerErrorMonitor.logError({
        type: 'decode',
        code: errorCode,
        message: error.message || '解码错误',
        context: {
          cid,
          vid,
        },
      });

      // 根据错误码获取错误信息
      const errorInfo = getErrorInfo(errorCode);

      // 更新播放器状态
      usePlayerStore.getState().setError(errorCode, 'decode', errorInfo);

      logger.error('[player] 解码错误:', {
        code: errorCode,
        message: errorInfo.message,
      });
    });
  }
}

// 导出单例实例
export { GlobalPlayerManager };
